import $ from 'jquery';
import Vuex from 'vuex'
import { createSite } from '../shared/components/Globalized';
import createPersistedState from 'vuex-persistedstate';
import { FUNNEL_STORAGE_KEY, getUserTimezoneOffsetVar } from '../shared/constants';
import { processError } from '../shared/utils';
import { questions as qFixture } from '../shared/fixtures/questions';
import { queston_options as qoFixture } from '../shared/fixtures/question_options';
const featureFlagApi = '/api/v5/feature_flag/';
const questionsApi = '/api/v5.4/questions/funnel/?randomize_options';

function persistencePlugin() {
  return createPersistedState({
    key: 'funnel-reg13',
    storage: window.sessionStorage
  })
}

// get bet data from the current funnel to be synchronized with the Landing Page data
function getInitialBet() {
  const data = JSON.parse(sessionStorage[FUNNEL_STORAGE_KEY]).nyop.bet;
  const site = createSite();
  const goalInLbs = site.convertedWeight(data.goal, getInitialWeightUnits()); // handle goal as lbs on the funnel and convert to KG before submit it
  return Object.assign({}, { ...data }, { goal: goalInLbs });
}

function getAlgorithmVersion() {
  return JSON.parse(sessionStorage[FUNNEL_STORAGE_KEY]).nyop.algorithmVersion
}

function getLPUrl() {
  return JSON.parse(sessionStorage[FUNNEL_STORAGE_KEY]).nyop.js_full_url
}

function getInitialWeightUnits() {
  return JSON.parse(sessionStorage[FUNNEL_STORAGE_KEY]).nyop.units
}

function getInitialHeightUnits() {
  return JSON.parse(sessionStorage[FUNNEL_STORAGE_KEY]).nyop.heightUnit
}

function getInitialEmail() {
  return sessionStorage.initialEmail
}

async function getFeatureFlag() {
  try {
    const result = await $.get(featureFlagApi)
    return result === true
  } catch (e) {
    return false
  }
}

async function fetchQuestionList() {
  try {
    return await $.get(questionsApi)
  } catch (e) {
    processError(null, undefined, false, e, false);
    throw e;
  }
}

function getInitialState() {
  return {
    nyop: {
      gender: '',
      pounds: null,
      feet: null,
      inches: null,
      units: getInitialWeightUnits(),
      heightUnit: getInitialHeightUnits(),
      algorithmVersion: getAlgorithmVersion(),
      answers: {},
      bet: getInitialBet(),
      js_full_url: getLPUrl(),
      user_timezone_offset: getUserTimezoneOffsetVar()
    },
    progressValue: 0,
    avgBet: 62,
    initialEmail: getInitialEmail(),
    loginRequireCaptchaFF: false,
    recaptchaLibraryReady: false,
    questionList: []
  }
}

function getInitialStore() {
  // prevent navigating to the funnel which needs Landing Page data without accessing there previously
  if (sessionStorage[FUNNEL_STORAGE_KEY] === undefined) {
    window.location.href = '/healthywager/';
    return;
  }

  return new Vuex.Store({
    plugins: [persistencePlugin()],
    state: getInitialState(),
    mutations: {
      resetStore: function(state) { Object.assign(state, getInitialState()) },
      updateUnits: function (state, newUnits) {
        state.nyop.units = newUnits === 'lb' ? 'lbs' : newUnits; // handle lbs and lb
        if (state.nyop.feet == null) state.nyop.heightUnit = state.nyop.units === 'lbs' ? 'in' : 'cm';
      },
      updateProgress: function (state, newValue) { state.progressValue = newValue },
      updateGender: function (state, newGender) { state.nyop.gender = newGender },
      updateGoal: function (state, newGoal) { state.nyop.bet.goal = newGoal },
      updateLength: function (state, newLwength) { state.nyop.bet.length = newLwength },
      updateWeight: function (state, newWeight) { state.nyop.pounds = newWeight },
      updateHeight: function (state, newHeight) { state.nyop.feet = newHeight.feet; state.nyop.inches = newHeight.inches; state.nyop.heightUnit = newHeight.heightUnit },
      updateAnswers: function (state, newValue) { state.nyop.answers = Object.assign({}, state.nyop.answers, newValue) },
      updateLoginRequireCaptchaFF: function (state, newValue) { state.loginRequireCaptchaFF = newValue },
      updateRecaptchaLibraryReady: function (state, newValue) { state.recaptchaLibraryReady = newValue },
      updateQuestionList: function (state, newValue) { state.questionList = newValue }
    },
    actions: {
      getFeatureFlagLoginRequireCaptchaFF: async function ({ commit }) {
        const response = await getFeatureFlag();
        commit('updateLoginRequireCaptchaFF', response);
        return response
      },
      activateRecaptchaLibraryReady: function ({ commit }) {
        commit('updateRecaptchaLibraryReady', true);
      },
      getQuestionList: async ({ commit }) => {
        let response;
        try {
          response = await fetchQuestionList();
        } catch (error) {
          // Lets the database return the data, otherwise use the fixture
          const questions = qFixture.map(q => ({ ...q.fields, id: q.pk })).filter(x => x.section === 'funnel');
          const questionIds = questions.map(q => q.id);
          const options = qoFixture.map(qo => ({ ...qo.fields, id: qo.pk })).filter(x => questionIds.includes(x.question_id));
          response = { questions, options };
        }
        commit('updateQuestionList', response);
        return response;
      }
    },
    getters: {
      getOptions: (state) => (questionSlug) => {
        if ((state.questionList.questions || []).some(q => q.slug === questionSlug)) {
          const questionId = state.questionList.questions.filter(q => q.slug === questionSlug)[0].id;
          const options = state.questionList.options.filter(x => x.question_id === questionId);
          return options;
        } else {
          // Lets the database return the data, otherwise use the fixture
          const question = qFixture.map(q => {
            if (q.fields.slug === questionSlug) {
              return q;
            }
          }).filter(q => !!q);
          const options = question && question.length > 0 ? qoFixture.map(qo => {
            if (qo.fields.question_id === question[0].pk) {
              return qo;
            }
          }).filter(qo => !!qo).map(qo => qo.fields) : [];
          return options;
        }
      },
      getQuestion: (state) => (questionSlug) => {
        if ((state.questionList.questions || []).some(q => q.slug === questionSlug)) {
          return state.questionList.questions.filter(q => q.slug === questionSlug)[0];
        } else {
          // Lets the database return the data, otherwise use the fixture
          const question = qFixture.map(q => {
            if (q.fields.slug === questionSlug) {
              return q;
            }
          }).filter(q => !!q);
          if (question && question.length > 0) {
            return question[0].fields;
          } else {
            throw Error(`The question with the '${questionSlug}' slug does not exists.`)
          }
        }
      }
    }
  });
}

export { getInitialStore };
